// The server can still emit codes outside SYNTAX_INDENT/SYNTAX_OTHER (legacy
// validation codes like EMPTY_CODE/UNSUPPORTED_LANGUAGE/UNSUPPORTED_TAB_WIDTH
// remain server-side as defensive 400s). Client-side only the codes below are
// modelled — anything else falls through to the catch-all toast.
export type ServerErrorCode =
  | 'SYNTAX_INDENT'
  | 'SYNTAX_OTHER'
  | 'CODE_TOO_LARGE'
  | 'TIMEOUT'
  | 'RATE_LIMITED'
  | 'AUTH_REQUIRED'
  | 'AUTH_INVALID'
  | 'INTERNAL'

export type ClientErrorCode = 'NETWORK_UNREACHABLE' | 'UNKNOWN' | 'NO_CODE'

export type ErrorCode = ServerErrorCode | ClientErrorCode

export type FormatErrorPayload = {
  code: ErrorCode
  error?: string
  line?: number
  column?: number
  reason?: string
}

// JS falls back to Monaco only on infra-style failures. Syntax errors and
// rate-limits are intentionally excluded — Monaco can't fix syntax, and
// falling back on rate-limit would reward spamming.
const FALLBACK_INFRA_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'NETWORK_UNREACHABLE',
  'INTERNAL',
  'TIMEOUT',
])

function syntaxToast(line?: number, column?: number, reason?: string): string {
  const where =
    line != null && column != null
      ? ` on line ${line}:${column}`
      : line != null
        ? ` on line ${line}`
        : ''
  if (!reason) return `Failed to parse code${where}.`
  return `Failed to parse code${where}: ${reason}`
}

export function formatToast(
  code: ErrorCode,
  line?: number,
  column?: number,
  reason?: string,
): string | null {
  switch (code) {
    case 'SYNTAX_INDENT':
    case 'SYNTAX_OTHER':
      return syntaxToast(line, column, reason)
    case 'NO_CODE':
      return 'No code available to format.'
    case 'AUTH_REQUIRED':
    case 'AUTH_INVALID':
      return 'Please log in to use formatter.'
    default:
      return 'Formatter unavailable.'
  }
}

export function shouldFallbackToMonaco(
  language: string,
  code: ErrorCode,
): boolean {
  if (language !== 'javascript') return false
  return FALLBACK_INFRA_CODES.has(code)
}

// Mirrors INDENT_MARKERS in services/formatter/src/errors.ts. Kept in sync
// manually because the formatter service is a separate package.
const LEGACY_INDENT_MARKERS = [
  'unindent does not match',
  'expected an indented block',
  'expected an indented statement',
  'unexpected indentation',
  'inconsistent use of tabs',
]

export type LegacyParsedError = {
  code: 'SYNTAX_INDENT' | 'SYNTAX_OTHER'
  line: number
  column: number
  reason: string
}

// Defensive fallback for older server builds that return only
// `{ error: "...Failed to parse code.py:L:C: <reason>..." }` without the
// structured `{ code, line, column, reason }` shape. Matches ruff first,
// then a generic "L:C: <reason>" pattern (gofmt / clang-format style).
export function parseLegacyFormatError(
  raw: string | undefined,
): LegacyParsedError | null {
  if (!raw) return null

  const ruff = raw.match(/Failed to parse [^:]*:(\d+):(\d+):\s*(.*)/)
  if (ruff) {
    const reason = ruff[3].trim()
    const code = LEGACY_INDENT_MARKERS.some((m) =>
      reason.toLowerCase().includes(m),
    )
      ? ('SYNTAX_INDENT' as const)
      : ('SYNTAX_OTHER' as const)
    return { code, line: Number(ruff[1]), column: Number(ruff[2]), reason }
  }

  const generic = raw.match(/(?:^|:|\s)(\d+):(\d+):\s*(.*)/)
  if (generic) {
    return {
      code: 'SYNTAX_OTHER',
      line: Number(generic[1]),
      column: Number(generic[2]),
      reason: generic[3].trim(),
    }
  }

  return null
}
